<template>
	<div
		v-if="shouldDisplay"
		:class="[
			`relative ${blok.height} ${blok.full_width ? 'full-bleed' : 'main-wrapper'} ${blok.visibility ? blok.visibility.join(' ') : ''} ${blok.text_color || ''}`
		]"
	>
		<!-- Background Image -->
		<NuxtImg
			:src="blok?.image?.[0]?.image?.filename"
			:alt="blok?.image?.[0]?.image?.alt || 'hero'"
			class="w-full h-full object-cover absolute inset-0"
			:class="blok?.image_position || ''"
			provider="storyblok"
			v-bind="imagePriority"
			format="webp"
			quality="80"
			sizes="sm:100vw md:100vw lg:100vw xl:1600px"
			:width="storyblokImgDimensions.width"
			:height="storyblokImgDimensions.height"
		/>

		<!-- Content Over Image -->
		<div
			:class="[
				`${blok.height} flex flex-col ${blockPosition.wrap} container mx-auto absolute inset-0`
			]"
		>
			<div
				class="w-full flex flex-col gap-4 p-5 lg:p-7.5"
				:class="`${blockPosition.content} ${contentRelativeStyles}`"
			>
				<div
					class="max-w-full overflow-hidden text-center"
					:class="`${blok.countdown_start_date ? '!leading-none' : 'leading-none'} ${arrayToString(blok.heading_styles) || 'text-4xl font-extralight'}`"
				>
					<StoryblokComponent
						v-for="heading in blok.heading"
						:key="heading._uid"
						:blok="heading"
					/>
				</div>
				<p
					v-if="blok.sub_heading"
					:class="arrayToString(blok.sub_heading_styles) || ''"
				>
					{{ blok.sub_heading }}
				</p>
				<StoryblokComponent
					v-for="call_to_action in blok.call_to_action"
					:key="call_to_action._uid"
					:blok="call_to_action"
				/>
				<CountdownBlock
					v-if="blok.countdown_start_date"
					:start-date="blok.countdown_start_date"
					:wrapper-class="blockPosition.countdown"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { BlokHero } from "~/types/storyblok/storyblok";
import { arrayToString } from "~/utils/arrayToString";

interface Props {
	blok: BlokHero;
}
const { blok } = defineProps<Props>();
const imagePriority = useBlokImagePriority();

const { shouldDisplay } = useDisplayBlock(blok.start_date, blok.end_date);

const storyblokImgDimensions = useStoryblokAssetDimensions(
	() => blok?.image?.[0]?.image?.filename
);
const contentRelativeStyles = computed(() =>
	blok?.height?.includes("pb-") || !blok?.height
		? "absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-full h-full"
		: ""
);
const blockPosition = computed(() => {
	let styles = {
		wrap: "justify-end",
		content: "items-center justify-center",
		countdown: "justify-center"
	};

	if (blok.content_position) {
		styles = {
			wrap: blok.content_position,
			content: `${blok.content_position} ${blok.content_offset || ""}`,
			countdown: blok.content_position
		};
	}

	return styles;
});
</script>
